import * as React from "react";

import { Link } from "react-router-dom";

import { Input, Checkbox, Form, Radio } from "antd";
import { Page, PageHero, Spinner } from "src/components/common";
import AuthorizationWrapper from "src/common/AuthorizationWrapper";

import pickListContext from "src/context/pickListContext";
import OrganizationAPI from "src/admin/Organization/API/OrganizationAPI";
import { Validation } from "src/common/utils";
import { Button } from "src/components/buttons";
import { AdminPermissions } from "src/common/Permissions";
import ClientAppConstants from "src/common/Constants";
import { toast } from "react-toastify";

interface IProps {
	history: any;
	match?: any;
	actions: any;
	organizationRole: RoleDTO;
}

interface IState {
	loading?: boolean;
	iconLoading?: boolean;
	role: RoleDTO;
	permissions: PermissionDTO[];
	isLoading: boolean;
}

class OrganizationRoleForm extends React.Component<IProps, IState> {
	public static contextType = pickListContext;
	public defaultRole = {
		id:
			this.props.match.params.roleId && this.props.match.params.roleId !== "-1"
				? this.props.match.params.roleId
				: undefined,
		name: "",
		description: "",
		permissionIds: [] as number[],
		isDefault: false,
		roleTypeId: undefined,
	};

	public state = {
		loading: false,
		iconLoading: false,
		role: this.defaultRole,
		permissions: [] as PermissionDTO[],
		isLoading: true,
	};

	public componentDidMount() {
		const organizationId = this.props.match.params.organizationId;
		const roleId = this.props.match.params.roleId;
		// roleId with -1 means it's a new role
		if (roleId !== "-1") {
			OrganizationAPI.getOrganizationRole({
				id: organizationId,
				roleId,
			}).then((role) => this.setState({ role, isLoading: false }));
		}

		OrganizationAPI.getPermissions()
			.then((permissions) => this.setState({ permissions, isLoading: false }))
			.catch((err) => toast.error("Something went wrong !"));
	}

	public render() {
		const roleId = this.props.match.params.roleId;
		const isNewOrganizationRole = roleId === "-1";

		const validRole = {
			roleType: Validation.validateIfANumberOrNumbers(
				this.state.role.roleTypeId!
			),
			roleName: Validation.validateStringIsNotEmpty(
				this.state.role.name,
				"Please enter a name"
			),
			description: Validation.validateStringIsNotEmpty(
				this.state.role.description,
				"Please enter a role description"
			),
		};

		const rolesRadioGroup = this.renderRoleRadioGroup(
			this.props.match.params.organizationId,
			validRole,
			+roleId
		);

		return this.state.isLoading !== false ? (
			<Spinner size="full-page" />
		) : (
			<Page title="Role details">
				<PageHero>
					<Link
						to={`/admin/organizations/${this.props.match.params.organizationId}/roles`}
					>
						&lt; ROLES
					</Link>
					<h1 className="mb-0">
						{isNewOrganizationRole ? "New Role" : `${this.state.role.name}`}
					</h1>
				</PageHero>
				<div className="container-fluid">
					<div className="row">
						<div className="col-6 form-group">
							<label htmlFor="role">Role type (need permission gate)</label>
							{rolesRadioGroup}
						</div>
					</div>
					<div className="row">
						<div className="col-6 form-group">
							<label htmlFor="role">Role name *</label>
							<Form.Item
								hasFeedback={true}
								validateStatus={validRole.roleName.validationStatus}
								help={validRole.roleName.message}
								required={true}
							>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditOrganizationRole]}
								>
									<Input
										placeholder="Role name"
										name="name"
										disabled={this.state.role.isDefault}
										value={this.state.role.name}
										onChange={this.updateRoleInfo}
										maxLength={50}
									/>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					<div className="row">
						<div className="col-6 form-group">
							<label htmlFor="description">Description</label>
							<Form.Item
								hasFeedback={true}
								validateStatus={validRole.description.validationStatus}
								help={validRole.description.message}
								required={true}
							>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditOrganizationRole]}
								>
									<Input.TextArea
										placeholder="Please enter a description!"
										name="description"
										disabled={this.state.role.isDefault}
										value={this.state.role.description}
										onChange={this.updateRoleInfo}
									/>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					<div className="row">
						<div className="col-9 form-group">
							<label>
								Select the following task permissions for this role:
							</label>
							<div className="row">
								{this.state.permissions.map(
									(permission: PermissionDTO, index) => {
										if (
											permission.permissionTypeId === this.state.role.roleTypeId
										) {
											return (
												<div key={index} className="col-4">
													<AuthorizationWrapper
														type="disable"
														permissions={[
															AdminPermissions.EditOrganizationRole,
														]}
													>
														<Checkbox
															id={
																permission.id ? permission.id.toString() : "0"
															}
															checked={this.permissionCheckedMapping(
																permission.id
															)}
															name={permission.name}
															ref={permission.name}
															onChange={this.onPermissionChange}
														>
															{permission.name}
														</Checkbox>
													</AuthorizationWrapper>
												</div>
											);
										} else {
											return;
										}
									}
								)}
							</div>
						</div>
					</div>
					<AuthorizationWrapper
						type="hide"
						permissions={[AdminPermissions.EditOrganizationRole]}
					>
						<div className="row">
							<div className="col">
								<Button
									className="btn-primary"
									loading={this.state.loading}
									onClick={this.onOrganizationRoleUpdate}
									disabled={!this.isValid(validRole)}
								>
									Save
								</Button>
							</div>
						</div>
					</AuthorizationWrapper>
				</div>
			</Page>
		);
	}

	private isValid = (validRole: any) => {
		const validateValues: string[] = Object.keys(validRole).map(
			(key) => validRole[key].validationStatus
		);
		return Validation.isValid(validateValues);
	};

	private updateRoleInfo = (evt: any) => {
		const role = { ...this.state.role };
		role[evt.target.name] = evt.target.value;
		this.setState({ role });
	};
	private updateRoleType = (evt: any) => {
		const role = { ...this.state.role };
		role[evt.target.name] = parseInt(evt.target.value, 10);
		this.setState({ role });
	};

	private permissionCheckedMapping = (permissionId: number | undefined) => {
		const match = this.state.role.permissionIds.find((p) => p === permissionId);
		if (match) {
			return true;
		} else {
			return false;
		}
	};

	private renderRoleRadioGroup = (
		organizationId: string,
		validRoles: any,
		roleId: number
	) => {
		const rolePickListType = this.context.pickListTypes.find(
			(type: PickListType) => type.code === "RoleType"
		) as PickListType;
		const rolePickListItems = this.filterRoles(
			+organizationId,
			rolePickListType.id!
		);
		const rolesRadioGroup = this.createRoleRadioGroup(
			validRoles,
			rolePickListItems,
			roleId
		);
		return rolesRadioGroup;
	};

	private createRoleRadioGroup = (
		validRole: any,
		roles: PickListItemResponseDTO[],
		roleId: number
	) => {
		const roleRadioGroup = (
			<Form.Item
				hasFeedback={true}
				validateStatus={validRole.roleType.validationStatus}
				help={validRole.roleType.message}
			>
				{
					<Radio.Group
						name="roleTypeId"
						onChange={this.updateRoleType}
						value={this.state.role.roleTypeId}
						disabled={roleId === -1 ? false : true}
					>
						{roles.map((role) => (
							<Radio key={role.id} value={role.id}>
								{role.description}
							</Radio>
						))}
					</Radio.Group>
				}
			</Form.Item>
		);
		return roleRadioGroup;
	};

	private filterRoles = (organizationId: number, pickListTypeId: number) => {
		if (organizationId === ClientAppConstants.DefaultOrgId) {
			const adminRoles = this.context.pickListItems.filter(
				(item: PickListItemResponseDTO) =>
					item.pickListTypeId === pickListTypeId
			);
			return adminRoles;
		}

		const defaultRoles = this.context.pickListItems
			.filter(
				(item: PickListItemResponseDTO) =>
					item.pickListTypeId === pickListTypeId
			)
			.filter(
				(foundItem: PickListItemResponseDTO) =>
					foundItem.code !== "SystemRoleType"
			);

		return defaultRoles;
	};

	private onPermissionChange = (evt: any) => {
		const organizationPermissionIds = [...this.state.role.permissionIds];

		// If an Edit permission is selected, add the associated View permission
		if (evt.target.name.startsWith("Edit") && evt.target.checked === true) {
			// Find the associated view checkbox by ref
			const viewCheckbox = this.refs[
				evt.target.name.replace("Edit", "View")
			] as Checkbox;
			if (viewCheckbox && viewCheckbox.props.checked === false) {
				const viewPermissionId = Number(viewCheckbox.props.id);
				// Add permission id to role
				if (organizationPermissionIds.includes(viewPermissionId) === false) {
					organizationPermissionIds.push(viewPermissionId);
				}
			}
		}

		const mapPermissionObj = this.state.permissions.find(
			(p) => p.name === evt.target.name
		)!;
		const permissionExistInOrganization = organizationPermissionIds.some(
			(p) => p === mapPermissionObj.id
		);
		// add new permission for organization
		if (!permissionExistInOrganization && evt.target.checked === true) {
			const newPermission: PermissionDTO = this.state.permissions.find(
				(p) => p.name === evt.target.name
			)!;
			organizationPermissionIds.push(newPermission.id!);
		}
		// remove existing permission from organization
		if (permissionExistInOrganization && evt.target.checked === false) {
			const existingPermission: PermissionDTO = this.state.permissions.find(
				(p) => p.name === evt.target.name
			)!;
			organizationPermissionIds.splice(
				organizationPermissionIds.indexOf(existingPermission.id!),
				1
			);
		}
		this.setState({
			role: { ...this.state.role, permissionIds: organizationPermissionIds },
		});
	};

	private onOrganizationRoleUpdate = () => {
		const { role } = this.state;
		const organizationId = this.props.match.params.organizationId;
		const request = {
			id: organizationId,
			roleId: role.id ? role.id : undefined,
			name: role.name,
			description: role.description,
			permissionIds: role.permissionIds,
			roleTypeId: role.roleTypeId,
		};
		this.setState({ loading: true });
		OrganizationAPI.updateOrganizationRole(request).then(() => {
			toast.success("Organization role updated");
			this.setState({ loading: false });
			this.props.history.push(`/admin/organizations/${organizationId}/roles`);
		});
	};
}

export default OrganizationRoleForm;
